import { MapToolBox } from "@/components/map-tool-box";
import { useDebounce } from "@/hooks/lib-ui";
import { paymentToolboxIcons } from "@/modules/parking-payment/components/parking-payment-map-tool-box-entry.const";
import { ParkingPaymentModeType, PaymentZoneArea } from "@/modules/parking-payment/payment-zone-area.type";
import { getAddress } from "@/services/web/mapbox.service";
import { BitmapLayer, GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import DeckGL from "@deck.gl/react";
import { mapboxStyle, ModeViewType } from "constants/map.const";
import { MAPBOX_ACCESS_TOKEN } from "constants/mapbox.const";
import { CityContext } from "contexts/city-context-provider";
import { PickingInfo, TileLayer } from "deck.gl";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import { bbox } from "@turf/turf";
import type { FeatureCollection, Point, Polygon, Position } from "geojson";
import { createMarkerLayer } from "helper/map-utils";
import { isNil } from "helper/utils";
import { useQueryParamByViewPort } from "hooks/use-set-query-params";
import { SearchPlace, SelectMapType, SwitchModeView } from "modules/curb-zone/components";
import { SearchFilterCurbZone } from "components/search-and-filter";
import { useSearchFilterCurbZone } from "@/hooks/use-search-filter-curb-zone";
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Map as StaticMap, ViewState } from "react-map-gl/mapbox";
import { ParkingMeter } from "services/web/api-parking-meter.type";
import { IconStation, MapType, mapUrlByType, ViewportPlaceType } from "types/map.type";
import { CurbZonePlaceTypeTooltip } from "./components/curb-zone-place-type-tooltip";
import { ParkingMeterDetail } from "./components/parking-meter-detail";
import { ParkingMeterModal } from "./components/parking-meter-modal";
import { PaymentZoneAreaCreationAside } from "./components/payment-zone-area-creation-aside";
import { PaymentZoneDrawingGuidelinePanel } from "./components/payment-zone-drawing-guideline-panel";
import { PaymentZoneAreaPromptModal } from "./components/payment-zone-area-prompt";
import { ParkingRateModal } from "./components/parking-rate-modal";
import { CityConfigModal } from "./components/city-config-modal";
import { FeeConfigsModal } from "./components/fee-config/fee-configs-modal";
import { ParkingRateAside } from "./components/parking-rate-aside";
import { RateSimulationModal } from "./components/rate-simulation-modal";
import { usePaymentZoneArea } from "./hooks/use-payment-zone-area";
import { usePaymentZoneCreation } from "./hooks/use-payment-zone-creation";
import { useQueryParkingMeter } from "./hooks/use-query-parking-meter";
import {
  initParkingMeter,
  overlapErrorMessage,
  ParkingMeterModalState,
  parkingPaymentMode,
  toastError,
} from "./parking-payment.const";
import { Toaster } from "@/components/ui/sonner";
import { useRemovePaymentZoneArea } from "./hooks/use-payment-zone-area-query";
import { useParkingRate } from "./hooks/use-parking-rate";
import { useCurbZonesLayer } from "./hooks/use-curb-zones-layer";

export const ParkingPaymentMap = () => {
  const { currentCity, viewport, setViewport } = useContext(CityContext);
  const [mapType, setMapType] = useState<MapType>(MapType.MAPBOX);
  const [modeView, setModeView] = useState<ModeViewType>("normal");
  const [parkingMeterDetail, setParkingMeterDetail] = useState<ParkingMeter | null>();
  const [parkingModalState, setParkingModalState] = useState<keyof typeof ParkingMeterModalState>(
    ParkingMeterModalState.DEACTIVATE,
  );
  const [bounding, setBounding] = useState<number[][]>();
  const nearBy = useDebounce(bounding, 500);

  const currentPosition = useRef<Position>([]);
  const setQueryParamByViewport = useQueryParamByViewPort();

  const { parkingMeters, createParkingMeter, updateParkingMeter, removeParkingMeter } = useQueryParkingMeter();

  const mapStyle = useMemo(() => mapboxStyle[modeView], [modeView]);

  const [appMode, setAppMode] = useState<ParkingPaymentModeType>(ParkingPaymentModeType.View);
  const [isCityConfigModalOpen, setIsCityConfigModalOpen] = useState(false);
  const [isFeeConfigsModalOpen, setIsFeeConfigsModalOpen] = useState(false);
  const [hiddenZoneIds, setHiddenZoneIds] = useState<Set<string>>(() => new Set());
  const [draftColor, setDraftColor] = useState<[number, number, number, number] | undefined>(undefined);

  // Determine if the payment zone panel is open
  const isPaymentZonePanelOpen = useMemo(
    () =>
      appMode === ParkingPaymentModeType.ManagePaymentZoneArea ||
      appMode === ParkingPaymentModeType.AddPaymentZoneArea ||
      appMode === ParkingPaymentModeType.EditPaymentZoneArea,
    [appMode],
  );

  // Determine if the parking rate panel is open
  const isParkingRatePanelOpen = useMemo(() => appMode === ParkingPaymentModeType.AddParkingRate, [appMode]);

  //  Handle mode change with specific logic for payment zone area modes
  // Centralizes the UI-specific toggling rules for switching between manage/add/view modes.
  const handleModeChange = useCallback<Dispatch<SetStateAction<ParkingPaymentModeType>>>(
    (mode) =>
      setAppMode((current) => {
        const next = typeof mode === "function" ? mode(current) : mode;

        if (next === ParkingPaymentModeType.CityConfig) {
          setIsCityConfigModalOpen(true);
          return ParkingPaymentModeType.View;
        }

        if (next === ParkingPaymentModeType.ManageFeeConfigs) {
          setIsFeeConfigsModalOpen(true);
          return ParkingPaymentModeType.View;
        }

        if (next === ParkingPaymentModeType.ManagePaymentZoneArea)
          return current === next ? ParkingPaymentModeType.View : next;

        if (next === ParkingPaymentModeType.AddParkingRate)
          return current === next ? ParkingPaymentModeType.View : next;

        return current === next && next === ParkingPaymentModeType.AddPaymentZoneArea
          ? ParkingPaymentModeType.ManagePaymentZoneArea
          : next;
      }),
    [setAppMode, setIsCityConfigModalOpen],
  );

  const {
    paymentZoneAreaDetail,
    openPaymentZoneAreaModal,
    paymentZoneAreaEditionData,
    showPaymentZoneAreaDetail,
    setPaymentAreaZoneEditionData,
    setPaymentZoneAreaModal,
    setPaymentZoneAreaDetail,
    paymentZoneAreaLayers,
    savePaymentZoneAreaProperties,
    hoveredFeatureId,
    findOverlappingZone,
    canRemoveSelectedPolygon,
    removeSelectedPolygon,
  } = usePaymentZoneArea({
    appMode,
    hiddenZoneIds,
    draftColor,
  });

  const paymentZones = useMemo(
    () =>
      paymentZoneAreaEditionData.features.filter((feature): feature is PaymentZoneArea =>
        Boolean((feature as PaymentZoneArea)?.id ?? (feature as PaymentZoneArea)?.name),
      ),
    [paymentZoneAreaEditionData],
  );

  const { removePaymentZoneArea } = useRemovePaymentZoneArea();

  const deletePaymentZoneAreaById = useCallback(
    async (zoneId: string) => {
      if (!zoneId) return;
      await removePaymentZoneArea(zoneId);
    },
    [removePaymentZoneArea],
  );

  const {
    draft,
    isSavingCreation,
    draftPolygonCount,
    startCreation,
    startEdition,
    updateDraft,
    beginDrawing,
    importPolygonsToDraft,
    cancelCreation,
    finishCreation,
    submitEdition,
  } = usePaymentZoneCreation({
    appMode,
    changeAppMode: handleModeChange,
    paymentZoneAreaEditionData,
    setPaymentAreaZoneEditionData,
    setPaymentZoneAreaDetail,
    savePaymentZoneAreaProperties,
    deletePaymentZoneArea: deletePaymentZoneAreaById,
    setDraftColor,
  });

  const {
    isParkingRateModalOpen,
    setIsParkingRateModalOpen,
    isReadOnly,
    selectedParkingRate,
    saveParkingRate,
    handlePublishParkingRate,
    handleSetParkingRateState,
    handleEditParkingRate,
    handleDuplicateParkingRate,
    handleViewParkingRate,
    handleDeleteParkingRate,
    handleCloseModal,
    rateToSimulate,
    handleSimulateParkingRate,
    handleCloseSimulation,
    parkingRates,
    parkingRatesLoading,
  } = useParkingRate();

  const { filter, changeFilter } = useSearchFilterCurbZone();
  const {
    curbZonePolygonsLayer,
    hoverInfo: curbZoneHoverInfo,
    handleCurbZoneHover,
  } = useCurbZonesLayer(nearBy, filter);

  const togglePaymentZoneVisibility = useCallback(
    (zone: PaymentZoneArea) => {
      setHiddenZoneIds((current) => {
        const next = new Set(current);
        if (next.has(zone.id)) {
          next.delete(zone.id);
        } else {
          next.add(zone.id);
        }
        return next;
      });

      if (paymentZoneAreaDetail?.id === zone.id) {
        setPaymentZoneAreaDetail(undefined);
      }
    },
    [paymentZoneAreaDetail?.id, setPaymentZoneAreaDetail],
  );

  const handleEditPaymentZone = useCallback(
    (zone: PaymentZoneArea) => {
      setPaymentZoneAreaDetail(zone);
      startEdition(zone);
      handleModeChange(ParkingPaymentModeType.EditPaymentZoneArea);
    },
    [handleModeChange, setPaymentZoneAreaDetail, startEdition],
  );

  const flyToPolygons = useCallback(
    (polygons: Polygon[]) => {
      if (!polygons.length) return;
      const collection: FeatureCollection = {
        type: "FeatureCollection",
        features: polygons.map((geometry) => ({ type: "Feature", properties: {}, geometry })),
      };
      const [minLng, minLat, maxLng, maxLat] = bbox(collection);

      // fitBounds divides by the bbox extent, so a degenerate (single-point / collinear) geometry
      // would yield a NaN viewport — center on it at a fixed zoom instead.
      if (maxLng <= minLng || maxLat <= minLat) {
        setViewport((prev) => ({
          ...prev,
          longitude: (minLng + maxLng) / 2,
          latitude: (minLat + maxLat) / 2,
          zoom: 16,
        }));
        return;
      }

      const mercator = new WebMercatorViewport({ ...viewport, width: window.innerWidth, height: window.innerHeight });
      const { longitude, latitude, zoom } = mercator.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        { padding: 80 },
      );
      if (!Number.isFinite(zoom)) return;
      setViewport((prev) => ({ ...prev, longitude, latitude, zoom: Math.min(zoom, 18) }));
    },
    [viewport, setViewport],
  );

  const handleImportGeoJson = useCallback(
    (polygons: Polygon[]): boolean => {
      const excludeId = draft?.mode === "edit" ? paymentZoneAreaDetail?.id : undefined;
      const overlapping = polygons.map((polygon) => findOverlappingZone(polygon, excludeId)).find(Boolean);
      if (overlapping) {
        toastError(overlapErrorMessage(overlapping, "Le GeoJSON"));
        return false;
      }
      importPolygonsToDraft(polygons);
      flyToPolygons(polygons);
      return true;
    },
    [draft?.mode, paymentZoneAreaDetail?.id, findOverlappingZone, importPolygonsToDraft, flyToPolygons],
  );

  const handleDeletePaymentZone = useCallback(
    async (zone: PaymentZoneArea) => {
      if (!zone?.id) return;
      await removePaymentZoneArea(zone.id);
      setPaymentZoneAreaDetail(undefined);
      handleModeChange(ParkingPaymentModeType.ManagePaymentZoneArea);
    },
    [handleModeChange, removePaymentZoneArea, setPaymentZoneAreaDetail],
  );

  const toolboxAppMode = isPaymentZonePanelOpen ? ParkingPaymentModeType.ManagePaymentZoneArea : appMode;

  useEffect(() => {
    if (showPaymentZoneAreaDetail) {
      setParkingMeterDetail(null);
    }
  }, [showPaymentZoneAreaDetail]);

  useEffect(() => {
    if (isNil(currentCity)) return;
    if (currentCity?.position) {
      setParkingMeterDetail(initParkingMeter);
      currentPosition.current = currentCity?.position;
    }
  }, [currentCity]);

  const updateParkingMetersViewport = (viewState: ViewState) => {
    if (!currentCity || !viewState) return;
    setQueryParamByViewport({ viewState, viewport, cityID: currentCity.id, setBounding });
  };

  const parkingMeterPoint = useMemo(() => {
    return parkingMeters.map((meter) => ({
      ...meter,
      status: meter.status ?? null,
      externalId: meter.externalId ?? null,
      batteryLevel: meter.batteryLevel ?? null,
      coordinates: meter.location.coordinates,
    }));
  }, [parkingMeters]);

  const parkingMeterLayer = new IconLayer({
    id: "parking-meter-icon-layer",
    data: parkingMeterPoint,
    pickable: parkingPaymentMode.includes(appMode),
    iconAtlas: "images/parking_meter_icon.png",
    iconMapping: {
      marker: { x: 0, y: 0, width: 40, height: 63, mask: true },
    },
    getIcon: () => "marker",
    sizeScale: 15,
    getPosition: (d: IconStation) => d.coordinates,
    getSize: () => 3,
    onClick: (info) => {
      if (info.object && info.object.id) {
        const { coordinates, ...rest } = info.object;
        rest["location"] = { type: "Point", coordinates };
        setParkingMeterDetail(rest);
        return;
      }
    },
    updateTriggers: {
      pickable: [appMode],
    },
  });

  const markerLayer = useMemo(() => {
    if (!parkingMeterDetail?.location?.coordinates?.length) {
      return null;
    }
    return createMarkerLayer({
      coordinates: [parkingMeterDetail.location.coordinates[0], parkingMeterDetail.location.coordinates[1]],
    });
  }, [parkingMeterDetail]);

  const currentCityLayer = new GeoJsonLayer({
    id: "current-city",
    data: currentCity?.boundaries,
    filled: false,
    lineWidthMinPixels: 1,
    lineWidthMaxPixels: 4,
  });

  const tileLayer = new TileLayer({
    id: "sat-other-map",
    data: mapUrlByType[mapType],

    minZoom: 0,
    maxZoom: 19,
    tileSize: 256,

    renderSubLayers: (props) => {
      const { boundingBox } = props.tile;

      return new BitmapLayer(props, {
        data: undefined,
        image: props.data,
        bounds: [boundingBox[0][0], boundingBox[0][1], boundingBox[1][0], boundingBox[1][1]],
      });
    },
    visible: mapType !== MapType.MAPBOX,
  });

  const parkingPaymentMapLayers = [
    tileLayer,
    curbZonePolygonsLayer,
    parkingMeterLayer,
    currentCityLayer,
    markerLayer,
    ...paymentZoneAreaLayers,
  ];

  const getCursor = () => {
    if ([ParkingPaymentModeType.AddParkingPayment, ParkingPaymentModeType.AddPaymentZoneArea].includes(appMode)) {
      return () => "cell";
    }
    if (appMode === ParkingPaymentModeType.EditPaymentZoneArea) {
      return () => {
        return hoveredFeatureId ? "pointer" : "default";
      };
    }
    return ({ isDragging }: { isDragging: boolean }) => (isDragging ? "grabbing" : "grab");
  };

  const handleDragEnd = (event: PickingInfo) => {
    if (!event.viewport) {
      return;
    }
    const viewport = event.viewport as unknown as ViewState;
    const newPosition = [viewport?.longitude, viewport?.latitude];
    currentPosition.current = [...newPosition];
    updateParkingMetersViewport(viewport);
  };

  const selectAddress = (viewport: ViewportPlaceType | null) => {
    if (viewport) {
      setParkingMeterDetail(
        (prevState) =>
          ({
            ...prevState,
            location: { type: "Point", coordinates: [Number(viewport.longitude), Number(viewport.latitude)] },
          }) as ParkingMeter,
      );

      setViewport((prevState) => ({ ...prevState, ...viewport, zoom: 16 }));
    }
  };

  const handleSelectParkingMeter = async (info: PickingInfo) => {
    if (!parkingPaymentMode.includes(appMode)) {
      return;
    }
    if (
      appMode === ParkingPaymentModeType.AddParkingPayment &&
      parkingModalState === ParkingMeterModalState.DEACTIVATE
    ) {
      //Add a new parking payment;
      const newPoint = { type: "Point", coordinates: info.coordinate } as Point;
      const address = await getAddress(newPoint);
      setParkingMeterDetail({
        ...initParkingMeter,
        address: {
          streetNumber: address?.number ?? "",
          streetName: address?.name ?? "",
          city: address.city ?? "",
          postalCode: address.postcode ?? "",
          country: address.country ?? "",
        },
        location: newPoint,
      });
      setParkingModalState(ParkingMeterModalState.SHOW);
    } else if (parkingModalState === ParkingMeterModalState.MINIMIZE) {
      setParkingMeterDetail({
        ...parkingMeterDetail,
        location: { type: "Point", coordinates: info.coordinate },
      } as ParkingMeter);
    }
  };

  const handleClose = () => {
    setParkingModalState(ParkingMeterModalState.DEACTIVATE);
    setParkingMeterDetail(null);
  };

  const saveParkingMeter = async (input: ParkingMeter) => {
    const newParkingMeter = { ...input, cityId: currentCity?.id ?? "" };
    if (input.id) {
      await updateParkingMeter({ variables: { updateParkingMeterInput: { ...newParkingMeter, id: input.id ?? "" } } });
    } else {
      await createParkingMeter({ variables: { createParkingMeterInput: newParkingMeter } });
    }
    handleClose();
  };

  const handleRemoveParkingMeter = async () => {
    await removeParkingMeter({ variables: { meterId: parkingMeterDetail?.id ?? "" } });
    setParkingMeterDetail(null);
  };

  return (
    <>
      <div className="relative h-full">
        <DeckGL
          initialViewState={{ ...viewport }}
          controller={{
            touchZoom: true,
            touchRotate: true,
            doubleClickZoom:
              appMode !== ParkingPaymentModeType.AddPaymentZoneArea &&
              appMode !== ParkingPaymentModeType.EditPaymentZoneArea,
          }}
          getCursor={getCursor()}
          layers={parkingPaymentMapLayers}
          pickingRadius={12}
          onDragEnd={handleDragEnd}
          onClick={handleSelectParkingMeter}
          onHover={handleCurbZoneHover}
          onViewStateChange={({ viewState }) => updateParkingMetersViewport(viewState as ViewState)}
        >
          <StaticMap mapboxAccessToken={MAPBOX_ACCESS_TOKEN} mapStyle={mapStyle} />
        </DeckGL>
        {curbZoneHoverInfo?.properties && (
          <CurbZonePlaceTypeTooltip
            left={curbZoneHoverInfo.x}
            top={curbZoneHoverInfo.y}
            curbZone={curbZoneHoverInfo.properties}
          />
        )}
        <div className="absolute top-16 left-6 z-6 flex gap-2">
          <SearchPlace onSelect={selectAddress} coordinates={viewport} />
          <SelectMapType mapType={mapType} onSelectMapType={(value) => setMapType(value)} />
          {currentCity && <SearchFilterCurbZone filter={filter} onChangeFilter={changeFilter} />}
        </div>
        <SwitchModeView modeView={modeView} selectModeView={(mode: ModeViewType) => setModeView(mode)} />
        <div className="absolute top-60 left-8 z-6">
          <MapToolBox
            disabled={parkingModalState !== ParkingMeterModalState.DEACTIVATE}
            setAppMode={handleModeChange}
            appMode={toolboxAppMode}
            toolboxIcons={paymentToolboxIcons}
          />
        </div>
        {parkingModalState !== ParkingMeterModalState.DEACTIVATE && parkingMeterDetail && (
          <ParkingMeterModal
            coordinates={viewport}
            parkingModalState={parkingModalState}
            setParkingModalState={setParkingModalState}
            parkingMeter={parkingMeterDetail}
            saveParkingMeter={saveParkingMeter}
            setParkingMeterDetail={setParkingMeterDetail}
            cancel={handleClose}
          />
        )}
        {parkingModalState !== ParkingMeterModalState.MINIMIZE &&
          parkingMeterDetail?.id &&
          !(paymentZoneAreaDetail && showPaymentZoneAreaDetail) && (
            <ParkingMeterDetail
              parkingMeter={parkingMeterDetail}
              setParkingModalState={setParkingModalState}
              handleRemoveParkingMeter={handleRemoveParkingMeter}
            />
          )}

        {openPaymentZoneAreaModal && (
          <PaymentZoneAreaPromptModal
            appMode={appMode}
            paymentZoneAreaDetail={paymentZoneAreaDetail}
            savePaymentZoneAreaProperties={savePaymentZoneAreaProperties}
            setPaymentZoneAreaModal={setPaymentZoneAreaModal}
            paymentZoneAreaEditionData={paymentZoneAreaEditionData}
            setPaymentAreaZoneEditionData={setPaymentAreaZoneEditionData}
          />
        )}

        {isCityConfigModalOpen && (
          <CityConfigModal cityId={currentCity?.id ?? ""} onClose={() => setIsCityConfigModalOpen(false)} />
        )}

        {isFeeConfigsModalOpen && <FeeConfigsModal onClose={() => setIsFeeConfigsModalOpen(false)} />}

        {isParkingRateModalOpen && (
          <ParkingRateModal
            selectedRate={selectedParkingRate}
            onClose={handleCloseModal}
            paymentZones={paymentZones}
            onSubmit={saveParkingRate}
            readOnly={isReadOnly}
          />
        )}

        {rateToSimulate && <RateSimulationModal rate={rateToSimulate} onClose={handleCloseSimulation} />}

        <ParkingRateAside
          open={isParkingRatePanelOpen}
          parkingRates={parkingRates}
          onAddClick={() => setIsParkingRateModalOpen(true)}
          onEditClick={handleEditParkingRate}
          onDuplicateClick={handleDuplicateParkingRate}
          onViewClick={handleViewParkingRate}
          onDeleteClick={handleDeleteParkingRate}
          onPublishClick={handlePublishParkingRate}
          onSetStateClick={handleSetParkingRateState}
          onSimulateClick={handleSimulateParkingRate}
          isLoading={parkingRatesLoading}
        />

        {draft && (draft.status === "drawing" || draft.mode === "edit") && (
          <PaymentZoneDrawingGuidelinePanel
            mode={draft.mode}
            name={draft?.name || ""}
            polygonCount={draftPolygonCount > 0 ? draftPolygonCount : undefined}
            isSaving={isSavingCreation}
            isAddingPolygons={draft.mode === "edit" && draft.status === "drawing"}
            canDeletePolygon={canRemoveSelectedPolygon}
            onDeletePolygon={removeSelectedPolygon}
            onCancel={cancelCreation}
            onFinish={draft.mode === "edit" ? submitEdition : finishCreation}
          />
        )}
        <PaymentZoneAreaCreationAside
          open={isPaymentZonePanelOpen}
          appMode={appMode}
          paymentZones={paymentZones}
          onEditZone={handleEditPaymentZone}
          onDeleteZone={handleDeletePaymentZone}
          hiddenZoneIds={hiddenZoneIds}
          onToggleZoneVisibility={togglePaymentZoneVisibility}
          draft={draft}
          onStartCreation={startCreation}
          onBeginDrawing={beginDrawing}
          onSubmitEdition={submitEdition}
          onDraftChange={updateDraft}
          onCancelCreation={cancelCreation}
          onImportGeoJson={handleImportGeoJson}
          isSavingCreation={isSavingCreation}
        />
      </div>
      <Toaster position="top-center" />
    </>
  );
};
